import { motion } from "framer-motion";
import { Star, MapPin, Phone, Globe, Orbit, TrendingUp, MousePointerClick, Navigation2 } from "lucide-react";
import { Reveal, SectionHead } from "./Reveal";
import { prefillQuote, GMB_MESSAGE } from "@/lib/scroll";

const POINTS = [
    { icon: TrendingUp, title: "Rank Higher Locally", desc: "Rich visual listings earn more attention in 'near me' searches — and Google notices." },
    { icon: MousePointerClick, title: "More Clicks & Calls", desc: "Listings with 360° tours get more direction requests, website visits and phone calls." },
    { icon: Orbit, title: "Street View Ready", desc: "Your tour can live right inside Google Maps, letting anyone step in from the street." },
];

const PHOTOS = [
    "https://images.unsplash.com/photo-1747380755783-61befe9b3da7?crop=entropy&cs=srgb&fm=jpg&q=85&w=400&h=300&fit=crop",
    "https://images.unsplash.com/photo-1733413788595-ab2e2e8c0a9c?crop=entropy&cs=srgb&fm=jpg&q=85&w=400&h=300&fit=crop",
    "https://images.unsplash.com/photo-1760072513357-9d450e935a80?crop=entropy&cs=srgb&fm=jpg&q=85&w=400&h=300&fit=crop",
];

export default function GoogleSection() {
    return (
        <section id="google" className="relative py-20 lg:py-32" data-testid="google-section">
            <div className="absolute -left-24 top-1/3 h-80 w-80 rounded-full bg-[#0072FF]/10 blur-[130px]" />
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <div className="grid items-center gap-14 lg:grid-cols-2">
                    <div>
                        <SectionHead
                            chapter="07"
                            eyebrow="Google Business Profile"
                            title="GET FOUND. GET EXPLORED. GET CHOSEN."
                            sub="When someone searches 'near me', your Google listing is your first impression. We supercharge it with a 360° tour so searchers don't just find you — they step inside."
                        />
                        <div className="mt-10 space-y-4">
                            {POINTS.map((p, i) => (
                                <motion.div
                                    key={p.title}
                                    data-testid={`google-point-${i}`}
                                    initial={{ opacity: 0, x: -28 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true, margin: "-40px" }}
                                    transition={{ duration: 0.6, delay: i * 0.08 }}
                                    className="glass-card flex items-start gap-4 rounded-2xl p-5"
                                >
                                    <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl border border-[#00C6FF]/40 text-[#00C6FF]">
                                        <p.icon size={19} />
                                    </span>
                                    <div>
                                        <h3 className="font-semibold text-white">{p.title}</h3>
                                        <p className="mt-1 text-sm text-slate-400">{p.desc}</p>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                        <Reveal delay={0.3}>
                            <button
                                data-testid="google-cta-btn"
                                onClick={() => prefillQuote(GMB_MESSAGE)}
                                className="cta-glow mt-9 rounded-full bg-[#00C6FF] px-8 py-4 text-sm font-bold uppercase tracking-[0.15em] text-[#050B14] transition-transform duration-300 hover:scale-105"
                            >
                                Create My Google Listing
                            </button>
                        </Reveal>
                    </div>

                    <Reveal delay={0.2}>
                        <div className="relative">
                            <div className="glass-card rounded-2xl p-6" data-testid="gmb-mock-card">
                                <div className="flex items-center gap-2 text-xs text-slate-400">
                                    <Globe size={13} className="text-[#00C6FF]" />
                                    google.com/maps · Business Profile preview
                                </div>
                                <div className="mt-4 flex items-start justify-between gap-3">
                                    <div>
                                        <h3 className="text-lg font-bold text-white">Your Business Name</h3>
                                        <p className="text-xs text-slate-400">360° Virtual Tour Service · Pune</p>
                                        <div className="mt-2 flex items-center gap-1.5">
                                            <span className="text-sm font-semibold text-white">5.0</span>
                                            {[...Array(5)].map((_, i) => (
                                                <Star key={i} size={14} className="fill-[#FFC107] text-[#FFC107]" />
                                            ))}
                                            <span className="text-xs text-slate-500">(128 reviews)</span>
                                        </div>
                                    </div>
                                    <span className="rounded-full border border-[#00C6FF]/40 bg-[#00C6FF]/10 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.15em] text-[#00C6FF]">
                                        Open 24/7
                                    </span>
                                </div>
                                <div className="mt-5 grid grid-cols-3 gap-2">
                                    {PHOTOS.map((src, i) => (
                                        <div key={i} className="relative overflow-hidden rounded-xl">
                                            <img src={src} alt="Business photo" loading="lazy" className="h-24 w-full object-cover" />
                                            {i === 2 && (
                                                <div className="absolute inset-0 flex items-center justify-center bg-[#050B14]/70">
                                                    <span className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-[#00C6FF]">
                                                        <Orbit size={13} className="orbit-ring" /> 360°
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                                <div className="mt-5 grid grid-cols-3 gap-2">
                                    {[
                                        { icon: Navigation2, label: "Directions" },
                                        { icon: Phone, label: "Call" },
                                        { icon: Globe, label: "Website" },
                                    ].map((b) => (
                                        <div
                                            key={b.label}
                                            className="flex items-center justify-center gap-2 rounded-full border border-[#00C6FF]/25 py-2.5 text-xs font-semibold text-[#00C6FF]"
                                        >
                                            <b.icon size={13} /> {b.label}
                                        </div>
                                    ))}
                                </div>
                                <div className="mt-4 flex items-center gap-2 rounded-xl bg-[#050B14]/60 px-4 py-3 text-xs text-slate-400">
                                    <MapPin size={13} className="flex-shrink-0 text-[#00C6FF]" />
                                    Your address here, Pune, Maharashtra
                                </div>
                            </div>
                            <div className="absolute -bottom-5 -right-4 rounded-2xl border border-[#00C6FF]/40 bg-[#050B14] px-5 py-3.5 shadow-[0_0_30px_rgba(0,198,255,0.3)]">
                                <p className="font-mono-accent text-[10px] tracking-[0.25em] text-[#00C6FF]">
                                    +2X ENGAGEMENT
                                </p>
                                <p className="text-xs text-slate-400">with virtual tours on listings</p>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}
